import React from "react";

import { toast } from "react-toastify";
import moment from "moment";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import mapboxgl from 'mapbox-gl';
import { FeatureCollection, Feature, Point, Position, BBox } from "geojson";

import DatePicker from "@components/DatePicker";
import DeviceWindow, { type CoefficientOverrideCallback, TrackInput } from "@screens/TracksScreen/DeviceWindow";
import DevicesMenu from "@screens/TracksScreen/DevicesMenu";
import Dropdown from "@components/MapDropdown";
import LeftBar from "@components/Layout/LeftBar";
import Map, { Source, Layer, Popup } from "@components/Map";
import Spinner from "@components/Spinner";
import { ContextProvider, useContext } from "@screens/TracksScreen/Context";
import { useStores } from "@stores";
import { AttachedDevice, deviceStatistics, deviceTrackDates, deviceTracksExportUrl, DeviceStatisticsResponse, DeviceType } from "@services/devicesService";
import {
  sensorFeatures, speedFeatures, potatoFeatures, notPotatoFeatures, sizeFeatures,
  moistureFeatures, fuelFeatures, deviceColorAt, isValidCoord, newDropStats, explainTrack,
  SENSOR_LAYER, SPEED_LAYER, POTATO_LAYER, NOT_POTATO_LAYER, SIZE_LAYER,
  MOISTURE_LAYER, FUEL_LAYER, VALUE_LAYERS, CAMERA_LINE_LAYERS, METRIC_FILL_LAYERS,
} from "@screens/TracksScreen/utils";
import { useLang } from "@src/hooks/useLang";
import TrackFilterControls from "@screens/TracksScreen/TrackFilterControls";
import { LayerLegend } from "@screens/TracksScreen/ValueLegend";
import TrackExportMenu, {
  downloadTrackExport,
  trackExportExt,
  type TTrackExportFormat,
} from "@screens/TracksScreen/TrackExportMenu";
import { hiddenSensorRanges, trackKeepFor, type TTrackFilter } from "@screens/TracksScreen/trackFilter";
import {
  TrackDayCache,
  TRACKS_RESPONSE_LIMIT,
  isTracksResponseTruncated,
} from "@screens/TracksScreen/trackLoad";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { track as trackEvent, EVENTS } from "@src/utils/analytics";
import { deviceFreshness, FRESHNESS_FILL } from "@src/utils/deviceOnline";
import MobileTabBar, { TAB_BAR_HEIGHT } from "@components/MobileTabBar";
import MobileDrawer from "@components/MobileDrawer";
import { ReactComponent as RouterIcon } from "@assets/icons/router.svg";

////////////////////////////////////////////////////////////////////////////////
// STYLES
////////////////////////////////////////////////////////////////////////////////

const StyleOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  background: rgba(256, 256, 256, 0.5);
  z-index: 10;
`;

// TODO(nk2ge5k): why do we need to declare this for every single screen?
const StyleSection = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  min-width: 0;
  align-items: stretch;
  overflow-y: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-height: var(--vh-full, 100vh);
  }
`;

const StyleMap = styled.div`
  height: 100vh;
  width: 100%;
  min-width: 0;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: calc(var(--vh-full, 100vh) - ${TAB_BAR_HEIGHT}px - var(--tabbar-inset, env(safe-area-inset-bottom, 0px)));
  }
`;

const MobileMenuButton = styled.button`
  position: fixed;
  bottom: calc(${TAB_BAR_HEIGHT + 16}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px)));
  left: 16px;
  height: 40px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 20px;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 13px;
  color: #494c5b;
  cursor: pointer;
  z-index: 100;
  svg {
    width: 16px;
    height: 16px;
  }
  path {
    fill: #68ad18;
  }
`;

const StyleActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #494c5b;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
  svg {
    width: 14px;
    height: 14px;
  }
`;

// Two-button toggle (value gradient / per-device color) for the map tracks.
const StyleColorToggle = styled.div`
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(137, 144, 147, 0.3);
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
`;

const StyleColorToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 12px;
  border: none;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  background: ${({ active }) => (active ? "#68ad18" : "#ffffff")};
  color: ${({ active }) => (active ? "#ffffff" : "#494c5b")};
  &:hover {
    background: ${({ active }) => (active ? "#68ad18" : "#f5f5f5")};
  }
`;

// Card holding the color / speed filter, in the right-hand control column.
const StyleFilterCard = styled.div`
  width: 220px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  font-family: Nunito Sans, sans-serif;
`;

// "Only the first N points" notice, in the right-hand control column.
const StyleNotice = styled.div`
  width: 220px;
  padding: 8px 10px;
  box-sizing: border-box;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: #6d4c00;
`;

// Legend mapping each plotted device to its color.
const StyleLegend = styled.div`
  position: absolute;
  z-index: 20;
  inset: 100px auto auto 24px;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 220px;
  max-height: 40vh;
  overflow-y: auto;
  font-family: Nunito Sans, sans-serif;
`;

const StyleLegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #494c5b;
  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const StyleLegendSwatch = styled.div<{ color: string }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ color }) => color};
`;

const emptyCollection: FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

const FIELDS_LAYER = "fields";

type ColorMode = "value" | "device";

// Fill expression that colors the sensor track by its normalized value
// (the original red→green heatmap). Extracted so it can be re-applied when the
// user toggles back from per-device coloring.
const SENSOR_VALUE_FILL: any = [
  "interpolate", ["linear"], ["get", "sensor"],
  0.0, "rgb(165.0, 0.0, 38.0)",
  5.0, "rgb(215.0, 48.0, 39.0)",
  10.0, "rgb(244.0, 109.0, 67.0)",
  15.0, "rgb(253.0, 174.0, 97.0)",
  20.0, "rgb(254.0, 224.0, 139.0)",
  35.0, "rgb(255.0, 255.0, 191.0)",
  45.0, "rgb(217.0, 239.0, 139.0)",
  55.0, "rgb(166.0, 217.0, 106.0)",
  62.5, "rgb(102.0, 189.0, 99.0)",
  80.0, "rgb(26.0, 152.0, 80.0)",
  90.0, "rgb(0.0, 104.0, 55.0)",
  99.0, "rgb(37.0, 37.0, 37.0)",
];

////////////////////////////////////////////////////////////////////////////////
// UTILITY FUNCTIONS
////////////////////////////////////////////////////////////////////////////////

// A single device's track ready to render: the device (for settings), the
// fetched statistics and the color assigned to it for this selection.
type DeviceTrack = {
  id: string;
  device: AttachedDevice;
  statistics: DeviceStatisticsResponse;
  color: string;
};

function mergeBBox(a: BBox | undefined, b: BBox | undefined): BBox | undefined {
  if (!b) return a;
  if (!a) return [b[0], b[1], b[2], b[3]];
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3]),
  ];
}

// userDevicesToPoints returns GeoJSON which can be used for displaying
// last location of the device on the map.
function userDevicesToPoints(devices: AttachedDevice[]): FeatureCollection<Point> {
  return {
    type: "FeatureCollection",
    features: devices
      .filter((device) => {
        const position = device.getLastPositionList();
        // A device that lost its GPS fix reports 0,0 — without this check its
        // marker lands in the Atlantic off West Africa.
        return position.length === 2 && isValidCoord(position[0], position[1]);
      })
      .map((device) => {
        const fill = FRESHNESS_FILL[deviceFreshness(device.getLastSeenAt()?.toDate())];

        return {
          type: "Feature",
          properties: {
            name: device.getName(),
            fill: fill,
          },
          geometry: {
            type: "Point",
            coordinates: device.getLastPositionList() as Position,
          }
        };
      }),
  };
}

// updateMap paints the tracks for every selected device. Each device's sensor
// and speed features are tagged with its color so the map can switch between
// value-based and per-device coloring without recomputing geometry.
//
// `filter` hides points (color bands on the sensor layer, a speed range on all
// layers); `fly` = false repaints in place, so toggling a filter does not move
// the camera away from what the user is looking at.
async function updateMap(
  map: mapboxgl.Map,
  tracks: DeviceTrack[],
  use_full_screen: boolean,
  from?: Date,
  to?: Date,
  filter?: TTrackFilter | null,
  fly: boolean = true,
) {
  const sensorSource = map.getSource(SENSOR_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const speedSource = map.getSource(SPEED_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const potatoSource = map.getSource(POTATO_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const notPotatoSource = map.getSource(NOT_POTATO_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const sizeSource = map.getSource(SIZE_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const moistureSource = map.getSource(MOISTURE_LAYER) as mapboxgl.GeoJSONSource | undefined;
  const fuelSource = map.getSource(FUEL_LAYER) as mapboxgl.GeoJSONSource | undefined;
  // The Mapbox style may still be loading, in which case the GeoJSON sources
  // aren't registered yet. Bail gracefully — the render effect re-runs once the
  // map is ready, so the track still paints.
  if (!sensorSource || !speedSource) return;

  const sensorFeaturesAll: Feature[] = [];
  const speedFeaturesAll: Feature[] = [];
  const potatoFeaturesAll: Feature[] = [];
  const notPotatoFeaturesAll: Feature[] = [];
  const sizeFeaturesAll: Feature[] = [];
  const moistureFeaturesAll: Feature[] = [];
  const fuelFeaturesAll: Feature[] = [];
  let bbox: BBox | undefined = undefined;

  const tagColor = (feature: Feature, color: string) => {
    feature.properties = { ...(feature.properties || {}), deviceColor: color };
  };

  for (const track of tracks) {
    const settings = track.device.getSettings();
    // The color range is a sensor-value filter, so it applies to the sensor
    // layer only; speed and turns apply to every layer. Built per machine
    // because the turn gate is each device's own analytics setting.
    const turnGate = settings?.getMinTurnRadiusM();
    const keepSensor = trackKeepFor(filter, true, turnGate);
    const keepOther = trackKeepFor(filter, false, turnGate);
    const width = settings?.getWidth() || 6;
    const delay = (settings?.getDelay() ?? 16000) / 1000;

    // Every stage that can drop a fix reports into `stats`, so a blank map
    // explains itself in the console instead of needing the pipeline bisected
    // by hand. One line per device, per paint.
    const stats = newDropStats();
    const sensorLine = sensorFeatures(
      track.statistics, width, delay, from, to,
      settings?.getIsStaticDevice() ?? false,
      stats,
      keepSensor,
    );
    explainTrack(
      `${track.device.getName() || track.device.getEsn()} (${track.device.getEsn()})`,
      stats,
    );
    for (const feature of sensorLine.features) {
      tagColor(feature, track.color);
      sensorFeaturesAll.push(feature);
    }
    bbox = mergeBBox(bbox, sensorLine.bbox);

    const speedLine = speedFeatures(track.statistics, from, to, keepOther);
    for (const feature of speedLine.features) {
      tagColor(feature, track.color);
      speedFeaturesAll.push(feature);
    }
    bbox = mergeBBox(bbox, speedLine.bbox);

    // Potato-camera metric layers. When the track has no camera data these
    // builders return empty collections, so the layers just stay blank.
    if (track.statistics.hasCameraData()) {
      const potatoLine = potatoFeatures(track.statistics, width, delay, from, to, keepOther);
      for (const feature of potatoLine.features) {
        tagColor(feature, track.color);
        potatoFeaturesAll.push(feature);
      }

      const notPotatoLine = notPotatoFeatures(track.statistics, width, delay, from, to, keepOther);
      for (const feature of notPotatoLine.features) {
        tagColor(feature, track.color);
        notPotatoFeaturesAll.push(feature);
      }

      const sizeLine = sizeFeatures(track.statistics, width, delay, from, to, keepOther);
      for (const feature of sizeLine.features) {
        tagColor(feature, track.color);
        sizeFeaturesAll.push(feature);
      }
    }

    // Grain moisture. Only the machines that carry the sensor report it; the
    // rest send a flat 0, which hasMoistureData() rejects.
    if (track.statistics.hasMoistureData()) {
      const moistureLine = moistureFeatures(track.statistics, width, delay, from, to, keepOther);
      for (const feature of moistureLine.features) {
        tagColor(feature, track.color);
        moistureFeaturesAll.push(feature);
      }
    }

    // Fuel sensor input (mV) — only devices an admin marked as having one.
    if (track.statistics.hasFuelData()) {
      const fuelLine = fuelFeatures(track.statistics, width, from, to, keepOther);
      for (const feature of fuelLine.features) {
        tagColor(feature, track.color);
        fuelFeaturesAll.push(feature);
      }
    }
  }

  sensorSource.setData({ type: "FeatureCollection", features: sensorFeaturesAll });
  speedSource.setData({ type: "FeatureCollection", features: speedFeaturesAll });
  potatoSource?.setData({ type: "FeatureCollection", features: potatoFeaturesAll });
  notPotatoSource?.setData({ type: "FeatureCollection", features: notPotatoFeaturesAll });
  sizeSource?.setData({ type: "FeatureCollection", features: sizeFeaturesAll });
  moistureSource?.setData({ type: "FeatureCollection", features: moistureFeaturesAll });
  fuelSource?.setData({ type: "FeatureCollection", features: fuelFeaturesAll });

  // A track can be painted and still be invisible if the camera never moves to
  // it, so report what went onto the map and whether it was flown to. Features
  // > 0 with no bbox means the geometry is there and the viewport is elsewhere.
  console.debug("[Tracks] painted", {
    sensorPolygons: sensorFeaturesAll.length,
    speedLines: speedFeaturesAll.length,
    bbox,
    willFlyTo: Boolean(bbox),
    activeSources: { potato: Boolean(potatoSource), moisture: Boolean(moistureSource) },
  });

  if (bbox && fly) {
    const camera = map.cameraForBounds(bbox as [number, number, number, number])!;
    map.flyTo({
      ...camera,
      essential: true,
      padding: {
        left: 30,
        top: use_full_screen ? 30 : 5,
        right: 30,
        bottom: use_full_screen ? 30 : window.innerHeight / 4,
      },
    });
  }
}

////////////////////////////////////////////////////////////////////////////////
// COMPONENTS
////////////////////////////////////////////////////////////////////////////////

type DevicePopup = {
  lng: number,
  lat: number,
  text: string,
};

// LoadedScreeen is a component that presents tracks after data loadad
// via API calls, this allows to omit any unecessary null checks.
const LoadedScreeen = observer(() => {
  const ctx = useContext();
  const { accountStore, settingsStore } = useStores();
  const lang = useLang();
  const isMobile = useIsMobile();
  const [map, setMap] = React.useState<mapboxgl.Map | null>(null);
  // Popup that shows on the map while hovering over device last location.
  const [popup, setDevicePopup] = React.useState<DevicePopup | null>(null);
  // Ids of the devices currently plotted, in selection order.
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  // Value layer currently shown on the map. Lifted out of the Dropdown so the
  // Details window can chart whatever the map is painting.
  const [activeLayer, setActiveLayer] = React.useState<string>(SENSOR_LAYER);
  // Date for which statistics will be selected (shared across all devices).
  const [date, setDate] = React.useState<Date>(moment().toDate());
  // Per-device statistics keyed by device id.
  const [statsByDevice, setStatsByDevice] = React.useState<Record<string, DeviceStatisticsResponse>>({});
  // Dates that have track data (union across selected devices).
  const [datesWithData, setDatesWithData] = React.useState<Date[]>([]);
  // Currently visible month on the calendar
  const [visibleMonth, setVisibleMonth] = React.useState<Date>(moment().toDate());
  // Mobile drawer state
  const [menuOpen, setMenuOpen] = React.useState(false);
  // How the map colors tracks: by sensor value (gradient) or by device.
  const [colorMode, setColorMode] = React.useState<ColorMode>("value");
  // Whether the user manually chose a color mode (stops the auto-default).
  const colorModeTouched = React.useRef(false);
  // Display filter — same controls and semantics as Fields → tracks. Kept when
  // the day or devices change, so a user can step through days with it on.
  const [colorFilterOn, setColorFilterOn] = React.useState(false);
  // Kept sensor range, 0–100 inclusive.
  const [sensorRange, setSensorRange] = React.useState<[number, number]>([0, 100]);
  const [speedFilterOn, setSpeedFilterOn] = React.useState(false);
  const [removeTurns, setRemoveTurns] = React.useState(false);
  const [speedMinKmh, setSpeedMinKmh] = React.useState<number | null>(null);
  const [speedMaxKmh, setSpeedMaxKmh] = React.useState<number | null>(null);
  // A switched-off toggle contributes nothing, whatever values it remembers.
  const trackFilter = React.useMemo<TTrackFilter>(() => ({
    sensorMin: colorFilterOn ? sensorRange[0] : null,
    sensorMax: colorFilterOn ? sensorRange[1] : null,
    speedMinKmh: speedFilterOn ? speedMinKmh : null,
    speedMaxKmh: speedFilterOn ? speedMaxKmh : null,
    removeTurns,
  }), [colorFilterOn, sensorRange, speedFilterOn, speedMinKmh, speedMaxKmh, removeTurns]);
  // The tracks the camera last flew to: a repaint for a filter change keeps
  // the view, a new day or device selection frames the new tracks.
  const flownFor = React.useRef<DeviceTrack[] | null>(null);
  // The chart's zoom window and the tracks it was set on (see the paint effect).
  const zoomRef = React.useRef<{ tracks: DeviceTrack[]; from: Date; to: Date } | null>(null);
  // An export in flight; the menu is disabled until the file arrives.
  const [exporting, setExporting] = React.useState(false);
  // Coefficients typed into the Details panel, per device, with the loaded day
  // they were typed for — the export uses one only for that same day.
  const typedCoefficients = React.useRef<
    Record<string, { statistics: DeviceStatisticsResponse; value: number } | undefined>
  >({});
  const onCoefficientOverride = React.useCallback<CoefficientOverrideCallback>(
    (deviceId, statistics, value) => {
      typedCoefficients.current[deviceId] = value == null ? undefined : { statistics, value };
    },
    [],
  );

  const points = userDevicesToPoints(ctx.devices);

  const selectedKey = selectedIds.join(",");

  // Auto-select the only device so single-device users keep a one-click flow.
  React.useEffect(() => {
    if (ctx.devices.length === 1 && selectedIds.length === 0) {
      setSelectedIds([ctx.devices[0].getId()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.devices.length]);

  // Default the color mode: per-device when several tracks share the map, by
  // value when there's a single track. Skipped once the user picks manually.
  React.useEffect(() => {
    if (colorModeTouched.current) return;
    setColorMode(selectedIds.length > 1 ? "device" : "value");
  }, [selectedIds.length]);

  const setColorModeManual = (mode: ColorMode) => {
    colorModeTouched.current = true;
    setColorMode(mode);
  };

  const toggleDevice = (device: AttachedDevice) => {
    const id = device.getId();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Tracks that have loaded statistics, in selection order (drives color).
  const renderTracks = React.useMemo<DeviceTrack[]>(() => {
    return selectedIds
      .map((id, index) => {
        const device = ctx.devices.find((d) => d.getId() === id);
        const statistics = statsByDevice[id];
        if (!device || !statistics) return null;
        return { id, device, statistics, color: deviceColorAt(index) };
      })
      .filter((t): t is DeviceTrack => t !== null);
  }, [selectedIds, statsByDevice, ctx.devices]);

  // Shape the loaded tracks for the bottom detail plot.
  const windowTracks = React.useMemo<TrackInput[]>(() => {
    return renderTracks.map((track) => ({
      id: track.id,
      name: track.device.getName() || track.device.getEsn().toUpperCase(),
      color: track.color,
      statistics: track.statistics,
      deviceType: track.device.getSettings()?.getType() ?? DeviceType.DEVICE_TYPE_RETROFIT,
      source: track.device.getSource(),
    }));
  }, [renderTracks]);

  const has_data = renderTracks.some((t) => t.statistics.getLength() > 0);
  // Strings added for John Deere combines and the row cap; a locale that does
  // not carry them yet falls back to the English text.
  const tracksLang = lang.tracks as Record<string, string | undefined>;
  // The endpoint stops at TRACKS_RESPONSE_LIMIT rows from the start of the
  // day, silently: the rest of the day is neither drawn nor in the Details
  // totals. Name the machines it happened to and where their data stops.
  const truncatedTracks = renderTracks.filter((t) =>
    isTracksResponseTruncated(t.statistics.getLength()),
  );
  const truncatedNotice = truncatedTracks.length === 0 ? null : (
    tracksLang.truncatedNotice ??
    "Only the first {limit} points of this day are shown for {names}, up to {time}. Later points are not drawn or counted in Details."
  )
    .replace("{limit}", TRACKS_RESPONSE_LIMIT.toLocaleString())
    .replace(
      "{names}",
      truncatedTracks.map((t) => t.device.getName() || t.device.getEsn().toUpperCase()).join(", "),
    )
    .replace(
      "{time}",
      // Where the earliest of them stops (rows come back oldest first).
      moment.unix(Math.min(...truncatedTracks.map((t) => {
        const times = t.statistics.getTimeList();
        return times[times.length - 1];
      }))).format("HH:mm"),
    );
  // A John Deere ribbon is colored by relative t/ha, not by a sensor reading.
  const jdTracks = renderTracks.filter((t) => t.device.getSource() === "johndeere").length;
  const sensorLayerTitle =
    jdTracks === 0
      ? lang.tracks.sensorLayerTitle
      : jdTracks === renderTracks.length
        ? tracksLang.yieldRelativeLayerTitle ?? "Yield (relative)"
        : tracksLang.sensorOrYieldLayerTitle ?? "Sensor / yield (relative)";
  // Camera metric layers appear only for accounts with the admin cameraInstalled
  // flag AND when a selected device actually carries per-point camera data.
  const cameraAvailable =
    accountStore.cameraInstalled &&
    renderTracks.some((t) => t.statistics.hasCameraData());
  // Moisture needs no account flag — only a machine that actually measured it.
  // Grain harvesters have the sensor; potato apparatus never does, and a device
  // whose sensor is unplugged reports zeros, which hasMoistureData() rejects.
  const moistureAvailable = renderTracks.some((t) => t.statistics.hasMoistureData());
  // Fuel: the device has the admin "fuel sensor connected" switch and readings.
  const fuelAvailable = renderTracks.some((t) => t.statistics.hasFuelData());
  // The day's readings behind the speed / moisture legend. Exact for one
  // machine; with several, each is still ramped over its own moisture range.
  const legendValues = React.useMemo(() => ({
    speeds: renderTracks.flatMap((t) => t.statistics.getSpeedList()),
    moistures: renderTracks.flatMap((t) => t.statistics.getMoistureList()),
  }), [renderTracks]);

  React.useEffect(() => {
    if (map !== null) {
      console.debug("updating fields");
      const source = map.getSource(FIELDS_LAYER) as mapboxgl.GeoJSONSource;
      source.setData(ctx.fields);
    }
  }, [map, ctx.fields]);

  // Fetch which dates have track data for the visible month (union of devices).
  React.useEffect(() => {
    if (selectedIds.length === 0) {
      setDatesWithData([]);
      return;
    }

    const from = moment(visibleMonth).startOf('month').toDate();
    const to = moment(visibleMonth).endOf('month').toDate();

    Promise.all(
      selectedIds.map((id) => deviceTrackDates(id, from, to).catch((e) => {
        console.error("ERROR [API.DeviceTrackDates]:", e);
        return [] as Date[];
      }))
    ).then((lists) => {
      const seen = new Set<string>();
      const out: Date[] = [];
      for (const list of lists) {
        for (const d of list) {
          const key = d.toDateString();
          if (!seen.has(key)) {
            seen.add(key);
            out.push(d);
          }
        }
      }
      setDatesWithData(out);
    });
  }, [selectedKey, visibleMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch (or refetch) track data for every selected device + the shared date.
  // NOTE: intentionally NOT gated on `map`. The data must be fetched as soon as
  // a device/date is selected, even if the Mapbox map hasn't fired its `load`
  // event yet. Painting is handled by a separate effect once `map` is ready.
  // Latest loadTracks call. Switching day or devices quickly used to let an
  // older, slower response land last and paint the previous day.
  const tracksReqRef = React.useRef(0);
  // Finished days already fetched, per (device, local day): going back to a
  // day, or adding a machine to the selection, no longer re-downloads every
  // selected device's points (trackLoad.ts).
  const dayCacheRef = React.useRef(new TrackDayCache<DeviceStatisticsResponse>());

  const loadTracks = React.useCallback(() => {
    const req = ++tracksReqRef.current;
    if (selectedIds.length === 0) {
      setStatsByDevice({});
      ctx.setLoading(false);
      return;
    }

    const from = moment(date).startOf('day').toDate();
    const to = moment(date).endOf('day').toDate();
    const cache = dayCacheRef.current;

    const cached: Record<string, DeviceStatisticsResponse> = {};
    const missing: string[] = [];
    for (const id of selectedIds) {
      const hit = cache.get(id, date);
      if (hit) cached[id] = hit;
      else missing.push(id);
    }
    if (missing.length === 0) {
      setStatsByDevice(cached);
      ctx.setLoading(false);
      return;
    }

    ctx.setLoading(true);
    console.time("track-load");

    Promise.all(
      missing.map((id) =>
        deviceStatistics(id, from, to, { yield: true })
          .then((response) => {
            // The window is cut at LOCAL midnight while `tracks.time` is device
            // time, so a device reporting in another zone can return 0 points
            // for a day the calendar dotted. Log the window that was actually
            // asked for alongside the count.
            console.debug("[Tracks] fetched", id, {
              points: response.getLength(),
              from: from.toISOString(),
              to: to.toISOString(),
              minSatellitesCount: response.getMinSatellites(),
            });
            return [id, response] as const;
          })
          .catch((e) => {
            console.error("ERROR [API.DeviceStatistics]:", id, e);
            return [id, null] as const;
          })
      )
    ).then((entries) => {
      if (req !== tracksReqRef.current) return;
      const next: Record<string, DeviceStatisticsResponse> = { ...cached };
      for (const [id, response] of entries) {
        if (!response) continue;
        next[id] = response;
        // A failed fetch is never cached, so the next visit asks again.
        cache.set(id, date, response);
      }
      setStatsByDevice(next);
    }).finally(() => {
      console.timeEnd("track-load");
      if (req === tracksReqRef.current) ctx.setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, date]);

  // The Reload button. It refetches the device list as well as the points:
  // each device's settings (delay, width) come with that list, which otherwise
  // loads once per visit — so a delay changed in device settings used to need
  // a full page reload before the tracks moved. Cached days go too: a John
  // Deere re-import can rewrite a finished day.
  const reload = () => {
    ctx.loadUserDevices().catch(() => undefined);
    dayCacheRef.current.clear();
    loadTracks();
  };

  // Effect responsible for fetching statistics whenever the selection or date
  // changes.
  React.useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  // Effect responsible for painting the fetched statistics onto the map. Runs
  // whenever the map becomes ready OR new statistics arrive, so the track paints
  // regardless of which finishes first.
  React.useEffect(() => {
    if (map === null) return;
    const fly = flownFor.current !== renderTracks;
    flownFor.current = renderTracks;
    // Keep the chart's zoom window while it still applies to these tracks, so
    // toggling a filter does not repaint the whole day under a zoomed chart.
    const zoom = zoomRef.current?.tracks === renderTracks ? zoomRef.current : null;
    updateMap(map, renderTracks, !zoom, zoom?.from, zoom?.to, trackFilter, fly).catch((e) => {
      console.error("ERROR [Map.updateTracks]:", e);
    });
  }, [map, renderTracks, trackFilter]);

  // Speed filter starts from the analytics default working band — 1–7 km/h
  // for potato harvesters, 1–15 for grain (analytics-schemas.ts defaults).
  const toggleSpeedFilter = (on: boolean) => {
    setSpeedFilterOn(on);
    if (on && speedMinKmh == null && speedMaxKmh == null) {
      const potato = renderTracks.some(
        (t) => t.device.getSettings()?.getType() === DeviceType.DEVICE_TYPE_POTATO,
      );
      setSpeedMinKmh(1);
      setSpeedMaxKmh(potato ? 7 : 15);
    }
  };

  // A metric can stop being available when the selection changes — a device
  // without a camera, or without a grain-moisture sensor. Leaving that layer
  // selected would show a blank map, so fall back to the sensor layer. Only the
  // layer that actually disappeared triggers this: a user watching speed must
  // not be bounced because some other metric went away.
  React.useEffect(() => {
    if (map === null) return;
    const lost =
      (!cameraAvailable && CAMERA_LINE_LAYERS.includes(activeLayer)) ||
      (!moistureAvailable && activeLayer === MOISTURE_LAYER) ||
      (!fuelAvailable && activeLayer === FUEL_LAYER);
    if (!lost) return;

    setActiveLayer(SENSOR_LAYER);
    try {
      VALUE_LAYERS.forEach((layer) => {
        map.setLayoutProperty(
          layer, "visibility",
          layer === SENSOR_LAYER ? "visible" : "none",
        );
      });
    } catch (e) {
      console.debug("visibility reset skipped:", e);
    }
  }, [map, cameraAvailable, moistureAvailable, fuelAvailable, activeLayer]);

  // Apply the active color mode to the sensor + speed layers. Per-device color
  // reads the `deviceColor` property tagged onto every feature.
  React.useEffect(() => {
    if (map === null) return;
    try {
      map.setPaintProperty(
        SENSOR_LAYER, "fill-color",
        colorMode === "device"
          ? ["coalesce", ["get", "deviceColor"], "#888888"]
          : SENSOR_VALUE_FILL,
      );
      map.setPaintProperty(
        SPEED_LAYER, "line-color",
        colorMode === "device"
          ? ["coalesce", ["get", "deviceColor"], "#888888"]
          : ["get", "line-color"],
      );
      // Camera and moisture layers are harvester-width fill layers colored by
      // their metric value, or by device when that color mode is active — same
      // rule as the sensor layer.
      for (const layer of METRIC_FILL_LAYERS) {
        map.setPaintProperty(
          layer, "fill-color",
          colorMode === "device"
            ? ["coalesce", ["get", "deviceColor"], "#888888"]
            : ["get", "metricColor"],
        );
      }
    } catch (e) {
      // Layers may not exist yet while the style is still loading; the effect
      // re-runs when `map` becomes available.
      console.debug("setPaintProperty skipped:", e);
    }
  }, [map, colorMode]);

  return (
    <>
      {ctx.loading ? <StyleOverlay><Spinner /></StyleOverlay> : null}
      <StyleSection>
        <LeftBar style={{ marginRight: 0 }} />
        <div style={{ width: "100%" }}>
          <StyleSection>
          {!isMobile && ctx.devices.length > 1
            ? <DevicesMenu
              hidden={false}
              selectedIds={selectedIds}
              onToggle={toggleDevice}
            /> : null}
            <StyleMap>
              {renderTracks.length > 0 && (
                <StyleLegend>
                  {renderTracks.map((track) => (
                    <StyleLegendRow key={track.id}>
                      <StyleLegendSwatch color={track.color} />
                      <span>{track.device.getName() || track.device.getEsn().toUpperCase()}</span>
                    </StyleLegendRow>
                  ))}
                </StyleLegend>
              )}
              {selectedIds.length > 0 && <div style={{
                position: "absolute",
                zIndex: 20,
                inset: "100px 24px auto auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: "8px",
              }}>
                <DatePicker
                  selected={date}
                  datesWithData={datesWithData}
                  onMonthChange={(month) => setVisibleMonth(month)}
                  onChange={(date) => {
                    if (date) {
                      setDate(date);
                    } else {
                      console.warn("No date selected");
                    }
                  }} />
                {truncatedNotice && <StyleNotice role="status">{truncatedNotice}</StyleNotice>}
                {has_data && (
                  <StyleColorToggle>
                    <StyleColorToggleButton
                      active={colorMode === "value"}
                      onClick={() => setColorModeManual("value")}
                    >
                      {lang.tracks.colorByValue ?? "By value"}
                    </StyleColorToggleButton>
                    <StyleColorToggleButton
                      active={colorMode === "device"}
                      onClick={() => setColorModeManual("device")}
                    >
                      {lang.tracks.colorByDevice ?? "By device"}
                    </StyleColorToggleButton>
                  </StyleColorToggle>
                )}
                {has_data && colorMode === "value" &&
                  (activeLayer === SPEED_LAYER || activeLayer === MOISTURE_LAYER) && (
                  <StyleFilterCard>
                    <LayerLegend
                      layer={activeLayer}
                      speedsKmh={legendValues.speeds}
                      moistures={legendValues.moistures}
                      moistureScaleOf={legendValues.moistures}
                    />
                  </StyleFilterCard>
                )}
                {has_data && (
                  <StyleFilterCard>
                    <TrackFilterControls
                      colorApplies={activeLayer === SENSOR_LAYER}
                      colorOn={colorFilterOn}
                      sensorMin={sensorRange[0]}
                      sensorMax={sensorRange[1]}
                      onColorToggle={setColorFilterOn}
                      onSensorRange={(min, max) => setSensorRange([min, max])}
                      speedOn={speedFilterOn}
                      speedMinKmh={speedMinKmh}
                      speedMaxKmh={speedMaxKmh}
                      onSpeedToggle={toggleSpeedFilter}
                      onSpeedRange={(min, max) => {
                        setSpeedMinKmh(min);
                        setSpeedMaxKmh(max);
                      }}
                      removeTurns={removeTurns}
                      onRemoveTurnsToggle={setRemoveTurns}
                    />
                  </StyleFilterCard>
                )}
                <StyleActionButton onClick={reload}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#68ad18" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 4v6h6" /><path d="M23 20v-6h-6" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                  </svg>
                  {lang.tracks.reload ?? "Reload"}
                </StyleActionButton>
                {has_data && (() => {
                  const exportTracks = renderTracks.filter(
                    (track) => track.statistics.getLength() > 0,
                  );
                  if (exportTracks.length === 0) return null;

                  const exportTrack = async (format: TTrackExportFormat, id?: string) => {
                    const track = exportTracks.find((t) => t.id === id) ?? exportTracks[0];
                    const from = moment(date).startOf('day').toDate();
                    const to = moment(date).endOf('day').toDate();
                    const name = (track.device.getName() || track.device.getEsn().toUpperCase())
                      .replace(/[\\/:*?"<>|\s]+/g, "_");
                    trackEvent(EVENTS.TRACKS_EXPORTED, { deviceId: track.id, format });
                    const typed = typedCoefficients.current[track.id];
                    setExporting(true);
                    try {
                      // The file matches the map: the color range only when the
                      // map is colored by sensor, as the swaths are filtered.
                      await downloadTrackExport(
                        deviceTracksExportUrl(track.id, from, to, format, {
                          units: settingsStore.units,
                          hiddenRanges: activeLayer === SENSOR_LAYER ? hiddenSensorRanges(trackFilter) : [],
                          speedMinKmh: trackFilter.speedMinKmh,
                          speedMaxKmh: trackFilter.speedMaxKmh,
                          removeTurns: trackFilter.removeTurns,
                          // The panel's typed coefficient replaces the
                          // calibration, in the file as on the screen.
                          coefficient: typed?.statistics === track.statistics ? typed.value : null,
                        }),
                        `${name}_${moment(date).format("YYYY-MM-DD")}.${trackExportExt(format)}`,
                      );
                    } catch (e: any) {
                      toast.error(e?.message ?? lang.fields?.tracksExportFailed ?? "Export failed");
                    } finally {
                      setExporting(false);
                    }
                  };

                  // One dropdown whatever the selection: pick a format, or with
                  // several machines plotted, a machine's format.
                  return (
                    <TrackExportMenu
                      variant="overlay"
                      label={lang.tracks.export ?? "Export"}
                      disabled={exporting}
                      targets={exportTracks.map((track) => ({
                        id: track.id,
                        label: track.device.getName() || track.device.getEsn().toUpperCase(),
                      }))}
                      onExport={(format, id) => void exportTrack(format, id)}
                    />
                  );
                })()}
              </div>}
              {has_data && <DeviceWindow
                tracks={windowTracks}
                activeLayer={activeLayer}
                onCoefficientOverride={onCoefficientOverride}
                onZoom={(from, to) => {
                  // Remember the window for the paint effect; a reset (no
                  // bounds) clears it.
                  zoomRef.current = from && to ? { tracks: renderTracks, from, to } : null;
                  if (!map) {
                    console.warn("Zoom update without map");
                    return;
                  }
                  // With the active filter: zooming used to repaint without it.
                  updateMap(map, renderTracks, false, from, to, trackFilter).catch(() => {
                    toast.error(lang.tracks.displayTrackError);
                  });
                }}
              />}
              <Map
                onLoad={(e) => {
                  const target = e.target;
                  setMap(target);

                  if (ctx.bbox !== null) {
                    target.fitBounds(ctx.bbox, {
                      linear: false,
                      maxDuration: 1,
                      padding: 30,
                    });
                  }

                  target.on("mousemove", "devices", (e) => {
                    const feature = (!!e.features) ? e.features[0] : null;
                    if (feature && feature.properties) {
                      const point = feature.geometry as Point;
                      setDevicePopup({
                        lng: point.coordinates[0],
                        lat: point.coordinates[1],
                        text: feature.properties["name"],
                      });
                    } else {
                      setDevicePopup(null);
                    }
                  });

                  target.on("mouseleave", "devices", () => setDevicePopup(null));
                }}
              >

                {has_data && <Dropdown
                    options={[
                      {
                        title: sensorLayerTitle,
                        value: SENSOR_LAYER,
                      },
                      {
                        title: lang.tracks.speedLayerTitle,
                        value: SPEED_LAYER,
                      },
                      ...(cameraAvailable ? [
                        {
                          title: lang.tracks.potatoLayerTitle ?? "Potatoes",
                          value: POTATO_LAYER,
                        },
                        {
                          title: lang.tracks.notPotatoLayerTitle ?? "Not potatoes",
                          value: NOT_POTATO_LAYER,
                        },
                        {
                          title: lang.tracks.sizeLayerTitle ?? "Average size",
                          value: SIZE_LAYER,
                        },
                      ] : []),
                      ...(moistureAvailable ? [
                        {
                          title: lang.tracks.moistureLayerTitle ?? "Grain moisture",
                          value: MOISTURE_LAYER,
                        },
                      ] : []),
                      ...(fuelAvailable ? [
                        {
                          title: lang.tracks.fuelLayerTitle ?? "Fuel sensor",
                          value: FUEL_LAYER,
                        },
                      ] : []),
                    ]}
                    selected={activeLayer}
                    onSelect={(value: string) => {
                      setActiveLayer(value);
                      if (map !== null) {
                        VALUE_LAYERS.forEach((layer) => {
                          map.setLayoutProperty(layer, "visibility",
                                                layer === value ? "visible" : "none");
                        });
                      }
                    }}
                  />
                }

                <Source id={FIELDS_LAYER} type="geojson" data={emptyCollection}>
                  <Layer {...{
                    source: FIELDS_LAYER,
                    type: 'fill',
                    id: FIELDS_LAYER,
                    paint: {
                      "fill-color": ["get", "fill"],
                      "fill-opacity": 0.5,
                      "fill-outline-color": ["get", "stroke"],
                    },
                  }}
                  />
                  <Layer {...{
                    source: FIELDS_LAYER,
                    type: "symbol",
                    layout: {
                      "text-field": ["get", "name"],
                      "text-variable-anchor": ["top", "bottom", "left", "right"],
                      "text-radial-offset": 0.5,
                      "text-justify": "auto",
                      "text-size": 20,
                    },
                    paint: {
                      "text-color": "#fff",
                      "text-halo-color": "#000",
                      "text-halo-width": 1,
                    }
                  }}
                  />
                </Source>
                <Source type="geojson" data={points}>
                  <Layer {...{
                    id: "devices",
                    type: "circle",
                    layout: {},
                    paint: {
                      "circle-radius": 5,
                      "circle-color": ["get", "fill"],
                      "circle-stroke-width": 1,
                      "circle-stroke-color": "rgb(255, 255, 255)",
                    },
                  }}
                  />
                </Source>
                <Source id={SPEED_LAYER} type="geojson" data={emptyCollection}>
                  <Layer {...{
                    source: SPEED_LAYER,
                    type: 'line',
                    id: SPEED_LAYER,
                    layout: {
                      'line-cap': 'round',
                      'line-join': 'round',
                      'visibility': 'none',
                    },
                    paint: {
                      'line-color': ["get", "line-color"],
                      'line-width': [
                        "interpolate", ["linear"], ["zoom"],
                        5, 1,
                        14, 5,
                        20, 10
                      ],
                    }
                  }}
                  />
                </Source>
                <Source id={SENSOR_LAYER} type="geojson" data={emptyCollection}>
                  <Layer {...{
                    source: SENSOR_LAYER,
                    type: 'fill',
                    id: SENSOR_LAYER,
                    layout: { 'visibility': 'visible' },
                    paint: {
                      "fill-outline-color": "rgba(0, 0, 0, 0)",
                      'fill-color': SENSOR_VALUE_FILL,
                    }
                  }}
                  />
                </Source>
                {METRIC_FILL_LAYERS.map((layerId) => (
                  <Source key={layerId} id={layerId} type="geojson" data={emptyCollection}>
                    <Layer {...{
                      source: layerId,
                      type: 'fill',
                      id: layerId,
                      layout: { 'visibility': 'none' },
                      paint: {
                        "fill-outline-color": "rgba(0, 0, 0, 0)",
                        'fill-color': ["get", "metricColor"],
                      }
                    }}
                    />
                  </Source>
                ))}
                {popup && (
                  <Popup
                    longitude={popup.lng}
                    latitude={popup.lat}
                    closeButton={false}
                    offset={5}
                  >
                    <span>{popup.text}</span>
                  </Popup>
                )}
              </Map>
            </StyleMap>
          </StyleSection>
        </div>
      </StyleSection>
      {isMobile && ctx.devices.length > 1 && (
        <>
          <MobileMenuButton onClick={() => setMenuOpen(true)}>
            <RouterIcon />
            {lang.menu.devicesMenuOption}
          </MobileMenuButton>
          <MobileDrawer open={menuOpen} onClose={() => setMenuOpen(false)}>
            <DevicesMenu
              hidden={false}
              selectedIds={selectedIds}
              onToggle={toggleDevice}
            />
          </MobileDrawer>
        </>
      )}
      {isMobile && <MobileTabBar />}
    </>
  );
});

const Screen: React.FC = () => {
  return <ContextProvider><LoadedScreeen /></ContextProvider>;
};

export default observer(Screen);
